import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { eventBus } from '../../utils/eventBus';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Bell } from 'lucide-react';
import { showToast } from '../../utils/errorHandler';
import { useTranslation } from 'react-i18next';
import { getReactionsUnreadCount, REACTION_BY_KEY, ENABLE_PASSPORT_REACTIONS } from '../../api/passportReactions';

// Types
interface User {
  id: string;
  name: string;
  subscription_type: string;
  is_recruiter: boolean;
}

interface Toast {
  message: string;
  type: 'success' | 'error' | 'info';
}

const InboxWidget: React.FC = () => {
  // SSE Connection: Uses getToken() for long-lived stream connection
  // This is the ONLY component that creates an SSE connection to /messaging/events
  // Other components (RecruiterInbox, Messages) receive updates via EventBus
  const { user, apiCall, isSignedIn, getToken, signOut } = useAuth(); // For SSE stream and regular API calls
  const { setUserProfile } = useUserProfile(); // Share profile across app
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  
  // State
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [sparksCount, setSparksCount] = useState<number>(0);
  const [sseConnected, setSseConnected] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);
  
  // Refs
  const eventSourceRef = useRef<EventSource | null>(null);
  const connectingRef = useRef<boolean>(false);
  const sseAbortRef = useRef<AbortController | null>(null);
  const connectTimeoutRef = useRef<number | null>(null);
  const retryCountRef = useRef<number>(0);
  const hasConnectedBefore = useRef<boolean>(false);
  const lastHeartbeatRef = useRef<number>(Date.now());

  // Use API Gateway (ngrok) for SSE to ensure auth/CORS works in browser
  const GATEWAY_BASE = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/v1`;
  
  // SSE retry configuration
  const maxRetries = 3;

  // Show toast notification
  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // SSE Connection
  const connectSSE = async () => {
    if (eventSourceRef.current || connectingRef.current) return; // prevent duplicate connections
    connectingRef.current = true;

    try {
      console.debug('🔌 InboxWidget: Starting SSE connection...');
      const token = await getToken(); // Use cached token to avoid rate limiting
      
      const controller = new AbortController();
      sseAbortRef.current = controller;

      const makeRequest = async (bearer: string | null) => {
        return fetch(`${GATEWAY_BASE}/messaging/events`, {
          method: 'GET',
          headers: {
            ...(bearer ? { 'Authorization': `Bearer ${bearer}` } : {}),
            'Accept': 'text/event-stream',
            'Cache-Control': 'no-cache',
            'ngrok-skip-browser-warning': 'true',
          },
          mode: 'cors',
          signal: controller.signal,
        });
      };

      let response = await makeRequest(token || null);
      if (response.status === 401) {
        try {
          const fresh = await getToken({ skipCache: true }); // Only refresh on 401
          response = await makeRequest(fresh || null);
        } catch {}
      }

      if (!response.ok) {
        console.error('❌ InboxWidget: SSE connection failed:', response.status);
        throw new Error(`SSE connection failed: ${response.status}`);
      }

      console.debug('✅ InboxWidget: SSE connection established, waiting for confirmation...');
      // DON'T reset retry count here - only reset when connection_established is received
      // Don't set as connected yet - wait for connection_established event
      // Mark as connecting to block further attempts
      // We don't have an EventSource instance; use a sentinel to block re-connects
      eventSourceRef.current = ({} as any);

      const stream = response.body;
      const reader = stream?.getReader();
      if (!reader) {
        throw new Error('No reader available');
      }

      console.debug('📡 InboxWidget: Starting to read SSE stream...');

      connectingRef.current = false;
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          console.debug('📡 InboxWidget: SSE stream ended');
          setSseConnected(false);
          eventSourceRef.current = null;
          sseAbortRef.current = null;
          
          // Auto-reconnect only if under retry limit
          if (currentUser && retryCountRef.current < maxRetries) {
            console.debug(`🔄 InboxWidget: SSE stream ended, will reconnect (attempt ${retryCountRef.current + 1}/${maxRetries})...`);
            retryCountRef.current++;
            setTimeout(() => {
              if (currentUser && !eventSourceRef.current) {
                console.debug('🔄 InboxWidget: Auto-reconnecting SSE...');
                connectSSE();
              }
            }, 3000);
          } else {
            console.debug('❌ InboxWidget: Max SSE retries reached or user logged out');
          }
          break;
        }

        const chunk = new TextDecoder().decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.trim() === '') continue; // Skip empty lines
          
          if (line.startsWith('data: ')) {
            try {
              const jsonData = line.slice(6).trim();
              const data = JSON.parse(jsonData);
              console.debug('📨 InboxWidget: SSE event received:', data);
              
              // Handle different event types
              switch (data.type) {
                case 'connection_established':
                  console.debug('✅ InboxWidget: SSE connection confirmed');
                  setSseConnected(true);
                  // Reset retry count on successful connection confirmation
                  retryCountRef.current = 0;
                  break;
                
                case 'heartbeat':
                  console.debug('💓 InboxWidget: Heartbeat received');
                  lastHeartbeatRef.current = Date.now();
                  break;
                
                case 'new_message': {
                  const senderId = data.message?.sender?.user_id || data.data?.sender?.user_id;
                  console.debug('📨 InboxWidget: New message received:', data);
                  console.debug('📨 InboxWidget: senderId:', senderId, 'currentUser.id:', currentUser?.id);
                  
                  // Ignore self-sent messages - improved comparison
                  if (senderId && currentUser && senderId === currentUser.id) {
                    console.debug('📨 InboxWidget: Ignoring self-sent message');
                    break;
                  }
                  
                  // PRIVACY SAFE: Use anonymized sender type
                  const senderType = data.message?.sender?.display_name || data.data?.sender?.display_name || 'Someone';

                  // On the messages page the Messages view is the source of truth:
                  // it marks the open chat read and emits an accurate
                  // `messaging:total_unread`. So only nudge the badge/toast when
                  // the user is NOT on /messages (matches the sparks handling),
                  // otherwise an incoming message for the chat you're reading
                  // would wrongly bump the inbox header badge.
                  if (window.location.pathname !== '/messages') {
                    showToast(`New message from ${senderType}`, 'info');
                    setUnreadCount(prev => {
                      const newCount = prev + 1;
                      console.debug('📨 InboxWidget: Updated unread count:', newCount);
                      return newCount;
                    });
                  }

                  // Emit global event for messaging page to react
                  const conversationId = data.message?.conversation_id || data.data?.conversation_id;
                  if (conversationId) {
                    eventBus.emit('messaging:new_message', {
                      conversation_id: conversationId,
                      message: data.message || data.data,
                      conversation_subject: data.message?.conversation_subject || data.data?.conversation_subject,
                      is_recruiter_reach_out: data.message?.is_recruiter_reach_out || data.data?.is_recruiter_reach_out
                    });
                  }
                  break;
                }
                
                case 'message_read':
                  console.debug('📨 InboxWidget: Message read event:', data);
                  // Could decrement unread count if needed
                  break;

                case 'passport_reaction': {
                  if (!ENABLE_PASSPORT_REACTIONS) break;
                  // Explorer-only feature — recruiters have no passport surface.
                  if (currentUser?.is_recruiter) break;
                  const def = data.reaction ? REACTION_BY_KEY[data.reaction] : null;
                  const emoji = def?.emoji || '✨';
                  showToast(`Someone vibed your passport ${emoji}`, 'info');
                  // Only nudge the badge when not already on the messages page.
                  if (window.location.pathname !== '/messages') {
                    setSparksCount((prev) => prev + 1);
                  }
                  eventBus.emit('messaging:passport_reaction', { reaction: data.reaction });
                  break;
                }

                case 'passport_reaction_removed': {
                  if (!ENABLE_PASSPORT_REACTIONS) break;
                  if (currentUser?.is_recruiter) break;
                  // A reactor withdrew their spark — re-sync the badge from source
                  // of truth and let the Messages page drop it from the list.
                  if (window.location.pathname !== '/messages') {
                    try {
                      const count = await getReactionsUnreadCount(apiCall);
                      setSparksCount(count);
                    } catch { /* badge self-corrects on next load */ }
                  }
                  eventBus.emit('messaging:passport_reaction', { removed: true });
                  break;
                }

                case 'spark_chat_started': {
                  if (!ENABLE_PASSPORT_REACTIONS) break;
                  if (currentUser?.is_recruiter) break;
                  // The owner opened a free chat from our spark — let A know even
                  // before any message arrives, and surface the new (unread) row.
                  if (window.location.pathname !== '/messages') {
                    showToast('Someone started a chat with you ✨', 'info');
                    setUnreadCount((prev) => prev + 1);
                  }
                  eventBus.emit('messaging:refresh_needed', {});
                  break;
                }

                default:
                  console.debug('📨 InboxWidget: Unknown SSE event:', data.type);
              }
            } catch (error) {
              console.error('❌ InboxWidget: Failed to parse SSE data:', error);
            }
        }
      }
      } // end while
    } catch (error: any) {
      // AbortError is expected when we intentionally disconnect (e.g. heartbeat timeout)
      if (error.name === 'AbortError') {
        console.debug('🔄 InboxWidget: SSE connection aborted (intentional disconnect for reconnect)');
        setSseConnected(false);
        eventSourceRef.current = null;
        return; // Don't retry, reconnect will be handled by caller
      }
      
      console.error('❌ InboxWidget: SSE connection error:', error);
      setSseConnected(false);
      eventSourceRef.current = null;
      
      // Increment retry count
      retryCountRef.current += 1;
      
      // Only retry if under max retries and user is still available
      if (retryCountRef.current < maxRetries && currentUser) {
        console.debug(`🔄 InboxWidget: Retrying SSE connection... (${retryCountRef.current}/${maxRetries})`);
        setTimeout(() => {
          if (currentUser) {
            connectSSE();
          }
        }, 5000);
      } else if (retryCountRef.current >= maxRetries) {
        console.error(`❌ InboxWidget: Max SSE retry attempts (${maxRetries}) reached. Stopping reconnection.`);
        showToast('Unable to connect to messaging service. Please refresh the page.', 'error');
      }
    } finally {
      connectingRef.current = false;
    }
  };

  // Disconnect SSE
  const disconnectSSE = () => {
    if (sseAbortRef.current) {
      try { sseAbortRef.current.abort(); } catch {}
      sseAbortRef.current = null;
    }
    eventSourceRef.current = null;
    connectingRef.current = false;
    setSseConnected(false);
    console.debug('🔌 InboxWidget: SSE disconnected');
  };

  // Load user profile
  const loadUserProfile = async (retryCount = 0) => {
    if (!user?.userId) return;
    
    try {
      console.debug('👤 InboxWidget: Loading user profile for:', user.userId, `(attempt ${retryCount + 1})`);
      
      // Use apiCall from custom useAuth hook (has auto-retry on 401)
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const response = await apiCall(`${apiBaseUrl}/api/v1/profiles/${user.userId}`);

      if (response.ok) {
        const profile = await response.json();
        console.debug('👤 InboxWidget: Loaded profile:', profile);
        
        const userData = {
          id: profile.id,
          name: profile.first_name || 'User',
          subscription_type: profile.subscription_type,
          is_recruiter: profile.is_recruiter,
        };
        
        setCurrentUser(userData);
        
        // Share profile with app-wide context (no extra API call needed)
        setUserProfile({
          id: profile.id,
          is_recruiter: profile.is_recruiter,
          is_admin: profile.is_admin === true,
          subscription_type: profile.subscription_type,
          first_name: profile.first_name,
          last_name: profile.last_name,
          email: profile.email,
          company_name: profile.company_name,
        });
      } else if (response.status === 403) {
        // Check if this is the "account deleted" error
        const errorData = await response.json().catch(() => ({}));
        if (errorData.detail === 'ACCOUNT_DELETED_CONTACT_SUPPORT') {
          console.error('🚨 InboxWidget: Account was deleted, cannot log in');
          // Store error in sessionStorage so it persists after redirect
          sessionStorage.setItem('accountDeletedError', 'true');
          // Immediately sign out and redirect - toast will show on login page
          await signOut();
          navigate('/login');
          return;
        }
        throw new Error('Failed to load profile');
      } else {
        throw new Error('Failed to load profile');
      }
    } catch (error) {
      console.error('❌ InboxWidget: Failed to load user profile:', error);
      setCurrentUser({
        id: user.userId,
        name: 'User',
        subscription_type: 'explorer_free',
        is_recruiter: false,
      });
    }
  };

  // Handle inbox click - redirect to Messages or Recruiter Inbox based on user type
  const handleInboxClick = () => {
    if (currentUser?.is_recruiter) {
      console.debug('📱 InboxWidget: Redirecting Recruiter to Recruiter Inbox');
      navigate('/recruiter-inbox');
    } else {
      console.debug('📱 InboxWidget: Redirecting to Messages page');
      navigate('/messages');
    }
    // Reset unread count when user opens messages
    setUnreadCount(0);
    setSparksCount(0);
  };

  // Load user profile when user signs in (with guard to prevent duplicate calls)
  useEffect(() => {
    const loadProfileWhenReady = async () => {
      if (!isSignedIn || !user) {
        return;
      }

      // Avoid duplicate attempts
      if (currentUser || hasAttemptedLoad) {
        return;
      }

      setHasAttemptedLoad(true);
      loadUserProfile();
    };

    loadProfileWhenReady();
  }, [isSignedIn, user?.userId]);

  // Connect to SSE when user is loaded
  useEffect(() => {
    if (currentUser) {
      // Connect to SSE when user is loaded (with delay to avoid blocking UI)
      // Always connect to SSE to receive real-time notifications
      if (connectTimeoutRef.current) {
        clearTimeout(connectTimeoutRef.current);
      }
      connectTimeoutRef.current = window.setTimeout(() => {
        console.debug('🚀 InboxWidget: Auto-connecting SSE for real-time notifications...');
        connectSSE();
      }, 1000);
    } else {
      // Disconnect SSE when user is not available
      disconnectSSE();
    }
  }, [currentUser]);

  // Tab visibility handling removed - SSE reconnection is handled by retry logic
  // to prevent infinite loops and rate limiting issues

  // Subscribe to total unread updates from Messages page
  useEffect(() => {
    const off = eventBus.on('messaging:total_unread', (evt: any) => {
      if (evt && typeof evt.total_unread === 'number') {
        console.debug(`📊 InboxWidget: Updated from Messages page: ${evt.total_unread}`);
        setUnreadCount(evt.total_unread);
      }
    });
    return () => off();
  }, []);

  // Subscribe to new message events as a fallback (in case SSE doesn't work)
  useEffect(() => {
    const off = eventBus.on('messaging:new_message', (evt: any) => {
      console.debug('📨 InboxWidget: Received messaging:new_message event via eventBus:', evt);
      // Only update if SSE is not connected (fallback mechanism)
      if (!sseConnected && window.location.pathname !== '/messages') {
        setUnreadCount(prev => {
          const newCount = prev + 1;
          console.debug('📨 InboxWidget: Updated unread count via eventBus fallback (SSE not connected):', newCount);
          return newCount;
        });
      } else {
        console.debug('📨 InboxWidget: Ignoring eventBus message (SSE connected or on messages page)');
      }
    });
    return () => off();
  }, [sseConnected]);

  // Load initial unread count from server when user is available
  useEffect(() => {
    const loadInitialUnreadCount = async () => {
      if (!currentUser) return;
      
      try {
        // Use new lightweight /unread endpoint instead of loading all conversations
        const response = await apiCall(`${GATEWAY_BASE}/messaging/unread`);

        if (response.ok) {
          const data = await response.json();
          const totalUnread = data.total_unread || 0;
          setUnreadCount(totalUnread);
          console.debug(`📊 InboxWidget: Loaded initial unread count: ${totalUnread}`);
        }
      } catch (error) {
        console.error('Failed to load initial unread count:', error);
      }
    };

    loadInitialUnreadCount();
  }, [currentUser]);

  // Load initial sparks (passport reactions) unread count
  useEffect(() => {
    const loadInitialSparks = async () => {
      if (!ENABLE_PASSPORT_REACTIONS) return;
      if (!currentUser || currentUser.is_recruiter) return;
      try {
        const count = await getReactionsUnreadCount(apiCall);
        setSparksCount(count);
      } catch (error) {
        console.debug('Failed to load sparks unread count:', error);
      }
    };
    loadInitialSparks();
  }, [currentUser]);

  // Clear sparks badge when Messages page marks them seen
  useEffect(() => {
    const off = eventBus.on('messaging:sparks_seen', () => setSparksCount(0));
    return () => off();
  }, []);

  // Refresh messages on SSE reconnect (to catch missed events)
  useEffect(() => {
    const refreshOnReconnect = async () => {
      // Only refresh if SSE just connected (not on initial mount)
      if (!sseConnected || !currentUser) return;
      
      // Skip refresh on first connect (initial page load)
      if (!hasConnectedBefore.current) {
        hasConnectedBefore.current = true;
        console.debug('✅ InboxWidget: First SSE connection - skipping refresh');
        return;
      }
      
      console.debug('🔄 InboxWidget: SSE reconnected - refreshing unread count to catch missed messages');
      
      try {
        const response = await apiCall(`${GATEWAY_BASE}/messaging/unread`);
        if (response.ok) {
          const data = await response.json();
          const totalUnread = data.total_unread || 0;
          setUnreadCount(totalUnread);
          console.debug(`📊 InboxWidget: Refreshed unread count after reconnect: ${totalUnread}`);

          // Also re-sync sparks (passport reactions) missed while disconnected
          if (ENABLE_PASSPORT_REACTIONS && !currentUser.is_recruiter) {
            try {
              const sparks = await getReactionsUnreadCount(apiCall);
              setSparksCount(sparks);
            } catch {}
          }

          // Notify other components to refresh their message lists
          eventBus.emit('messaging:refresh_needed', {});
        }
      } catch (error) {
        console.error('Failed to refresh after SSE reconnect:', error);
      }
    };

    refreshOnReconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sseConnected]);

  // Heartbeat timeout detection - reconnect if no heartbeat for 60s
  useEffect(() => {
    if (!sseConnected) return;

    const interval = setInterval(() => {
      const timeSinceLastHeartbeat = Date.now() - lastHeartbeatRef.current;
      
      if (timeSinceLastHeartbeat > 60000 && sseConnected) {
        console.warn('⚠️ InboxWidget: No heartbeat for 60s - reconnecting SSE');
        disconnectSSE();
        connectSSE();
      }
    }, 15000); // Check every 15 seconds

    return () => clearInterval(interval);
  }, [sseConnected]);

  // Cleanup SSE connection on unmount
  useEffect(() => {
    return () => {
      if (connectTimeoutRef.current) {
        clearTimeout(connectTimeoutRef.current);
        connectTimeoutRef.current = null;
      }
      disconnectSSE();
    };
  }, []);

  if (!currentUser) return null;

  return (
    <>
      {/* Toast Notifications */}
      {toast && (
        <div className={`fixed top-4 right-4 z-[10001] px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg shadow-lg text-white text-xs sm:text-sm ${
          toast.type === 'success' ? 'bg-green-500' : 
          toast.type === 'error' ? 'bg-red-500' : 'bg-blue-500'
        }`}>
          {toast.message}
        </div>
      )}

      {/* Inbox Widget */}
      <div className="relative">
        <button
          onClick={handleInboxClick}
          className="relative p-1 sm:p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
          title="Messages"
        >
          <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
          
          {(unreadCount + sparksCount) > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-pink-500 text-white text-[7px] sm:text-[9px] rounded-full min-w-[14px] h-[14px] sm:min-w-[16px] sm:h-[16px] flex items-center justify-center font-bold px-0.5 leading-none">
              {(unreadCount + sparksCount) > 99 ? '99+' : (unreadCount + sparksCount)}
            </span>
          )}
          
          <div className={`absolute -bottom-0.5 -right-0.5 sm:bottom-0 sm:right-0 w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full border-2 border-white ${
            sseConnected ? 'bg-emerald-400' : 'bg-slate-300'
          }`} title={sseConnected ? 'Connected' : 'Disconnected'} />
        </button>
      </div>
    </>
  );
};

export default InboxWidget;